import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { residentApi, errMsg } from "@/lib/api";
import { useResident } from "@/lib/residentAuth";
import { BottomSheet } from "@/components/BottomSheet";
import { Field, BigInput } from "@/components/form";
import { VISIT_SLOTS } from "@/lib/visits";

const DAYS = 14;
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const istNow = () => new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));

function nextDays() {
  const now = istNow();
  return Array.from({ length: DAYS + 1 }, (_, i) => { const d = new Date(now); d.setDate(now.getDate() + i); return d; });
}

function DatePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="visit-dates">
      {nextDays().map((d, i) => {
        const v = iso(d);
        const on = v === value;
        return (
          <button key={v} type="button" data-testid={`visit-date-${v}`} onClick={() => onChange(v)}
            className={`shrink-0 w-16 py-2 rounded-xl border text-center pg-tap ${on ? "bg-[#131e2e] border-[#131e2e] text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            <span className="block text-[11px] font-semibold uppercase">{i === 0 ? "Today" : d.toLocaleDateString("en-IN", { weekday: "short" })}</span>
            <span className="block text-lg font-extrabold leading-tight">{d.getDate()}</span>
            <span className="block text-[11px]">{d.toLocaleDateString("en-IN", { month: "short" })}</span>
          </button>
        );
      })}
    </div>
  );
}

function SlotPicker({ date, value, onChange }: { date: string; value: string; onChange: (v: string) => void }) {
  const isToday = date === iso(istNow());
  const hour = istNow().getHours();
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="visit-slots">
      {VISIT_SLOTS.map((s) => {
        const past = isToday && Number(s.value.slice(0, 2)) <= hour;
        const on = s.value === value;
        return (
          <button key={s.value} type="button" disabled={past} data-testid={`visit-slot-${s.value}`} onClick={() => onChange(s.value)}
            className={`h-11 rounded-xl border text-xs font-semibold pg-tap disabled:opacity-35 disabled:line-through ${on ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>{s.label}</button>
        );
      })}
    </div>
  );
}

export function VisitSheet({ l, open, onClose, onBooked }: { l: any; open: boolean; onClose: () => void; onBooked: () => void }) {
  const { resident } = useResident();
  const [f, setF] = useState<any>({ date: iso(istNow()), slot: "", name: resident?.name || "", phone: resident?.phone || "", note: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await residentApi.post("/resident/visits", { ...f, property_id: l.id });
      toast.success("Visit requested. The owner will confirm by email.");
      onBooked(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ok = f.slot && f.name.trim() && /^[6-9]\d{9}$/.test(f.phone);
  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && onClose()} title="Book a visit" description={l.name} testId="visit-sheet">
      <div className="space-y-4">
        <Field label="Pick a day"><DatePicker value={f.date} onChange={(date) => setF({ ...f, date, slot: "" })} /></Field>
        <Field label="Pick a time"><SlotPicker date={f.date} value={f.slot} onChange={(slot) => setF({ ...f, slot })} /></Field>
        <Field label="Your full name"><BigInput data-testid="visit-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Mobile number"><BigInput data-testid="visit-phone-input" inputMode="numeric" maxLength={10} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value.replace(/\D/g, "") })} /></Field>
        <Field label="Note for the owner (optional)"><Textarea data-testid="visit-note-input" rows={2} maxLength={300} className="rounded-xl text-base bg-white" placeholder="e.g. Coming with my parents" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <Button data-testid="visit-submit-btn" disabled={busy || !ok} onClick={send} className="w-full h-14 rounded-xl text-base bg-[#131e2e] hover:bg-[#0b1320]">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request visit"}
        </Button>
      </div>
    </BottomSheet>
  );
}
